import React, { useState, useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { setPageTitle } from '../../utils/pageTitle';
import { LayoutDashboard, Database, Shield, ChevronLeft, Command, Menu, X, MessageSquare } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useDarkMode } from '../../context/DarkModeContext';
import { Badge } from '../ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { UserInfoSection } from '../layout/UserInfoSection';
import { employeeNamesService } from '../../services/employeeNames';
import { getAllowedEmails } from '../../services/allowedEmails';
import { FeedbackModal } from '../shared/FeedbackModal';
import '../../styles/employee-directory.css';

export const EmployeeDirectoryLayout: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isDarkMode } = useDarkMode();
  const [userProfile, setUserProfile] = useState<any>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isAdmin, setIsAdmin] = useState(false);
  const [displayName, setDisplayName] = useState<string>('');
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);

  useEffect(() => {
    // Get user profile information and check admin status
    if (user && user.email) {
      setUserProfile({
        name: user.displayName || user.email?.split('@')[0] || 'User',
        email: user.email,
        role: 'User' // Will be updated based on admin check
      });

      // Load display name for UserInfoSection
      const loadDisplayName = async () => {
        const name = await employeeNamesService.getNameFromEmail(user.email!);
        if (name) {
          setDisplayName(name);
        } else {
          setDisplayName(user.displayName || user.email?.split('@')[0] || 'User');
        }
      };
      loadDisplayName();

      // Check if user is admin using JLS Apps admin system
      const checkAdminStatus = async () => {
        try {
          const allowedEmails = await getAllowedEmails();
          const userRecord = allowedEmails.find(record => record.email.toLowerCase() === user.email!.toLowerCase());
          const isUserAdmin = userRecord?.isAdmin || false;
          setIsAdmin(isUserAdmin);
          
          // Update user profile with correct role
          setUserProfile((prev: any) => ({
            ...prev,
            role: isUserAdmin ? 'Admin' : 'User'
          }));
        } catch (error) {
          console.error('Error checking admin status:', error);
          setIsAdmin(false);
        }
      };

      checkAdminStatus();
    } else {
      setIsAdmin(false);
    }
  }, [user]);

  // Handle window resize for responsive behavior
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (mobile) {
        setIsMobileMenuOpen(false);
      }
    };

    handleResize(); // Set initial state
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []); // Empty dependency array to prevent re-renders

  // Set page title based on current route
  useEffect(() => {
    const path = location.pathname;
    let title = 'Employee Directory';
    
    if (path === '/employee-directory' || path === '/employee-directory/dashboard') {
      title = 'Employee Directory';
    } else if (path.startsWith('/employee-directory/directory')) {
      title = 'Directory | Employee Directory';
    } else if (path.startsWith('/employee-directory/globe')) {
      title = 'Globe | Employee Directory';
    }
    
    setPageTitle(title);
  }, [location.pathname]);

  return (
    <div
      className="min-h-screen bg-background employee-directory"
      style={{ backgroundColor: isDarkMode ? '#111827' : undefined }}
    >
      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-50 bg-background border-b shadow-sm">
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center space-x-3">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#3B82F6"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="10" cy="7" r="4"/>
              <path d="M10.3 15H7a4 4 0 0 0-4 4v2"/>
              <circle cx="17" cy="17" r="3"/>
              <path d="m21 21-1.9-1.9"/>
            </svg>
            <h1 className="text-lg font-bold text-[#2563EB] dark:text-[#3B82F6]">JLS Directory</h1>
          </div>
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            {isMobileMenuOpen ? (
              <X className="h-6 w-6" />
            ) : (
              <Menu className="h-6 w-6" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu Backdrop */}
      {isMobileMenuOpen && (
        <div
          className="md:hidden fixed inset-0 z-40 bg-black bg-opacity-50"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed left-0 top-0 h-full bg-background border-r shadow-lg transition-all duration-300 flex flex-col
          ${isMobile
            ? `z-50 w-64 ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}`
            : `z-40 ${sidebarCollapsed ? 'w-14' : 'w-60'}`
          }
        `}
        style={{ backgroundColor: isDarkMode ? '#1F2937' : undefined }}
      >
        {/* Desktop Sidebar Toggle Button - Hidden on mobile */}
        <button
          className="hidden md:block absolute -right-3 top-6 z-20 h-6 w-6 rounded-full border shadow-md transition-colors duration-200 flex items-center justify-center"
          style={{
            backgroundColor: isDarkMode ? '#1F2937' : 'white',
            borderColor: isDarkMode ? '#374151' : '#e5e7eb'
          }}
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
        >
          <ChevronLeft
            className={`h-4 w-4 transition-transform duration-300 ${sidebarCollapsed ? 'rotate-180' : ''}`}
            style={{ color: isDarkMode ? '#9CA3AF' : '#6B7280' }}
          />
        </button>

        {(sidebarCollapsed && !isMobile) ? (
          /* Collapsed: Only show larger web app logo */
          <div className="p-2 flex items-center justify-center h-full">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="48"
              height="48"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#3B82F6"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="10" cy="7" r="4"/>
              <path d="M10.3 15H7a4 4 0 0 0-4 4v2"/>
              <circle cx="17" cy="17" r="3"/>
              <path d="m21 21-1.9-1.9"/>
            </svg>
          </div>
        ) : (
          <>
            {/* Upper Section - Title, logo, profile pic, email, role */}
            <div className="p-6 space-y-6">
              {/* Web App Logo and Title */}
              <div className="flex items-center space-x-3">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#3B82F6"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="10" cy="7" r="4"/>
                  <path d="M10.3 15H7a4 4 0 0 0-4 4v2"/>
                  <circle cx="17" cy="17" r="3"/>
                  <path d="m21 21-1.9-1.9"/>
                </svg>
                <h2 className="text-xl font-bold text-[#2563EB] dark:text-[#3B82F6]">JLS Directory</h2>
              </div>
              
              {/* Company Logo */}
              <div className="flex justify-center">
                <img
                  src="/images/jls-logo.png"
                  alt="JLS Trading Co. Logo"
                  className="w-24 h-12 object-contain shadow-lg rounded company-logo"
                  style={{ borderRadius: '0.375rem !important', transform: 'none !important' }}
                  onError={(e) => {
                    // Fallback to placeholder if image doesn't load
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                    const fallback = target.nextElementSibling as HTMLElement;
                    if (fallback) fallback.style.display = 'flex';
                  }}
                />
                {/* Fallback placeholder - hidden by default */}
                <div
                  className="w-24 h-12 bg-gradient-to-r from-primary to-primary/80 rounded flex items-center justify-center text-primary-foreground font-bold text-sm"
                  style={{ display: 'none' }}
                >
                  JLS LOGO
                </div>
              </div>
              
              {/* User Info */}
              <div className="text-center">
                <Avatar className="w-12 h-12 mx-auto mb-2 avatar-small">
                  <AvatarImage src={user?.photoURL || ''} alt="Profile" />
                  <AvatarFallback className="bg-primary/10 text-primary font-semibold text-lg">
                    {user?.displayName?.charAt(0) || user?.email?.charAt(0)}
                  </AvatarFallback>
                </Avatar>
                <p className="text-sm font-medium text-foreground">
                  {user?.displayName || 'User'}
                </p>
                <p className="text-xs text-muted-foreground">
                  {user?.email}
                </p>
                {userProfile?.role && (
                  <Badge variant="secondary" className="mt-1">
                    {userProfile.role}
                  </Badge>
                )}
              </div>
            </div>

            {/* First Divider */}
            <div className="mx-4 border-t border-gray-200 dark:border-gray-700" />

            {/* Navigation - flex-1 to take remaining space */}
            <nav className="flex-1 mt-4 overflow-y-auto flex flex-col">
              {/* Main Navigation - Dashboard and Employee Database */}
              <div
                onClick={() => {
                  navigate('/employee-directory/dashboard');
                  if (isMobile) setIsMobileMenuOpen(false);
                }}
                className={`nav-link ${
                  location.pathname === '/employee-directory/dashboard' || location.pathname === '/employee-directory'
                    ? 'active'
                    : ''
                }`}
              >
                <LayoutDashboard className="mr-3 h-5 w-5 flex-shrink-0" />
                Dashboard
              </div>

              <div
                onClick={() => {
                  navigate('/employee-directory/directory');
                  if (isMobile) setIsMobileMenuOpen(false);
                }}
                className={`nav-link ${
                  location.pathname === '/employee-directory/directory'
                    ? 'active'
                    : ''
                }`}
              >
                <Database className="mr-3 h-5 w-5 flex-shrink-0" />
                Employee Database
              </div>

              <div
                onClick={() => {
                  setIsFeedbackModalOpen(true);
                  if (isMobile) setIsMobileMenuOpen(false);
                }}
                className="nav-link"
              >
                <MessageSquare className="mr-3 h-5 w-5 flex-shrink-0" />
                Feedback
              </div>

              {/* Second Divider */}
              <div className="mx-4 my-4 border-t border-gray-200 dark:border-gray-700" />

              {/* Utility Navigation - Apps Hub and Admin */}
              <div
                onClick={() => {
                  navigate('/dashboard');
                  if (isMobile) setIsMobileMenuOpen(false);
                }}
                className="nav-link"
              >
                <Command className="mr-3 h-5 w-5 flex-shrink-0" />
                Apps Hub
              </div>

              {/* Admin Link - Only shown to admin users */}
              {isAdmin && (
                <div
                  onClick={() => {
                    navigate('/admin');
                    if (isMobile) setIsMobileMenuOpen(false);
                  }}
                  className="nav-link"
                >
                  <Shield className="mr-3 h-5 w-5 flex-shrink-0" />
                  Admin
                  <Shield className="ml-auto h-4 w-4 text-amber-500" />
                </div>
              )}
            </nav>

            {/* User Info Section at the bottom */}
            <UserInfoSection
              displayName={displayName}
              isCollapsed={false}
            />
          </>
        )}
      </div>

      {/* Main Content */}
      <div
        className={`transition-all duration-300 employee-directory
          ${isMobile
            ? 'pt-20 px-4 pb-8'
            : `p-8 ${sidebarCollapsed ? 'ml-14' : 'ml-60'}`
          }
        `}
        style={{ backgroundColor: isDarkMode ? '#111827' : undefined }}
      >
        <div className="max-w-6xl mx-auto">
          <Outlet />
        </div>
      </div>

      {/* Feedback Modal */}
      <FeedbackModal
        open={isFeedbackModalOpen}
        onOpenChange={setIsFeedbackModalOpen}
        appName="Employee Directory"
      />
    </div>
  );
};

export default EmployeeDirectoryLayout;